import type { DashboardMetricsResponse } from '../../../types/dashboard';
import { collectGoalsByMetric } from '../goalsByMetric';
import type {
  ClientAcquisitionPoint,
  RevenuePoint,
  SafeTCombinedSeries,
  SafeTOrgSeries,
  SafeTSecuritySeries,
  SafeTDashboardData,
  TulsaPoint,
} from './types';
import { MAX_QUARTERS_CHART } from '../constants';

const asNumber = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

function createEmptyOrgSeries(key: 'skyway' | 'droneport'): SafeTOrgSeries {
  const displayName = key === 'skyway' ? 'Skyway Range' : 'DronePort Network';
  return {
    key,
    displayName,
    clientAcquisition: [],
    revenue: [],
    tulsa: [],
    tulsaAllTime: {
      totalRevenue: 0,
      tulsaRevenue: 0,
      totalProducers: 0,
      tulsaProducers: 0,
    },
    goalsByMetric: {},
  };
}

const byQuarter = <T extends { quarter: string }>(series: T[], quarter: string): T | undefined =>
  series.find((point) => point.quarter === quarter);

function recomputeCombined(
  skyway: SafeTOrgSeries,
  droneport: SafeTOrgSeries
): SafeTCombinedSeries {
  const quartersSet = new Set([
    ...skyway.clientAcquisition.map((p) => p.quarter),
    ...droneport.clientAcquisition.map((p) => p.quarter),
  ]);
  const quarterList = Array.from(quartersSet)
    .sort((a, b) => a.localeCompare(b))
    .slice(-MAX_QUARTERS_CHART);

  const clientAcquisition: ClientAcquisitionPoint[] = quarterList.map((quarter) => {
    const sky = byQuarter(skyway.clientAcquisition, quarter);
    const drone = byQuarter(droneport.clientAcquisition, quarter);
    return {
      quarter,
      engaged: asNumber(sky?.engaged) + asNumber(drone?.engaged),
      acquired: asNumber(sky?.acquired) + asNumber(drone?.acquired),
      internationalTestingAcquired:
        asNumber(sky?.internationalTestingAcquired) + asNumber(drone?.internationalTestingAcquired),
    };
  });

  const revenue: RevenuePoint[] = quarterList.map((quarter) => {
    const sky = byQuarter(skyway.revenue, quarter);
    const drone = byQuarter(droneport.revenue, quarter);
    return {
      quarter,
      revenueNewClients: asNumber(sky?.revenueNewClients) + asNumber(drone?.revenueNewClients),
      revenueAllClients: asNumber(sky?.revenueAllClients) + asNumber(drone?.revenueAllClients),
    };
  });

  const tulsa: TulsaPoint[] = quarterList.map((quarter) => {
    const sky = byQuarter(skyway.tulsa, quarter);
    const drone = byQuarter(droneport.tulsa, quarter);
    return {
      quarter,
      totalRevenue: asNumber(sky?.totalRevenue) + asNumber(drone?.totalRevenue),
      tulsaRevenue: asNumber(sky?.tulsaRevenue) + asNumber(drone?.tulsaRevenue),
      totalProducers: asNumber(sky?.totalProducers) + asNumber(drone?.totalProducers),
      tulsaProducers: asNumber(sky?.tulsaProducers) + asNumber(drone?.tulsaProducers),
    };
  });

  const tulsaAllTime = {
    totalRevenue:
      asNumber(skyway.tulsaAllTime.totalRevenue) + asNumber(droneport.tulsaAllTime.totalRevenue),
    tulsaRevenue:
      asNumber(skyway.tulsaAllTime.tulsaRevenue) + asNumber(droneport.tulsaAllTime.tulsaRevenue),
    totalProducers:
      asNumber(skyway.tulsaAllTime.totalProducers) + asNumber(droneport.tulsaAllTime.totalProducers),
    tulsaProducers:
      asNumber(skyway.tulsaAllTime.tulsaProducers) + asNumber(droneport.tulsaAllTime.tulsaProducers),
  };

  const latestSky = skyway.clientAcquisition[skyway.clientAcquisition.length - 1];
  const latestSkyRev = skyway.revenue[skyway.revenue.length - 1];
  const latestSkyTulsa = skyway.tulsa[skyway.tulsa.length - 1];
  const latestDrone = droneport.clientAcquisition[droneport.clientAcquisition.length - 1];
  const latestDroneRev = droneport.revenue[droneport.revenue.length - 1];
  const latestDroneTulsa = droneport.tulsa[droneport.tulsa.length - 1];

  const byOrgLatest = {
    skyway: {
      engaged: asNumber(latestSky?.engaged),
      acquired: asNumber(latestSky?.acquired),
      revenueAllClients: asNumber(latestSkyRev?.revenueAllClients),
      tulsaRevenue: asNumber(latestSkyTulsa?.tulsaRevenue),
      totalRevenue: asNumber(latestSkyTulsa?.totalRevenue),
    },
    droneport: {
      engaged: asNumber(latestDrone?.engaged),
      acquired: asNumber(latestDrone?.acquired),
      revenueAllClients: asNumber(latestDroneRev?.revenueAllClients),
      tulsaRevenue: asNumber(latestDroneTulsa?.tulsaRevenue),
      totalRevenue: asNumber(latestDroneTulsa?.totalRevenue),
    },
  };

  return {
    clientAcquisition,
    revenue,
    tulsa,
    tulsaAllTime,
    byOrgLatest,
  };
}

/**
 * Maps dashboard API payloads into typed Safe-T card data.
 */
export function mapApiToSafeTData(payloads: DashboardMetricsResponse[]): SafeTDashboardData {
  const skyway = createEmptyOrgSeries('skyway');
  const droneport = createEmptyOrgSeries('droneport');
  const tuSecurity: SafeTSecuritySeries = { risk: [], poams: [] };

  for (const payload of payloads) {
    const section = payload.sections.find((entry) => entry.sectionId === 'safeT');
    if (!section) continue;

    const byId = new Map(section.metrics.map((metric) => [metric.metricId, metric]));

    if (payload.groupId === 'TU - SAFE-T') {
      const risk = byId.get('safeT.riskPosture');
      if (risk) {
        tuSecurity.risk = risk.series.slice(-MAX_QUARTERS_CHART).map((point: Record<string, unknown>) => ({
          quarter: String(point.quarter),
          appliedLow: asNumber(point.appliedLow),
          appliedMedium: asNumber(point.appliedMedium),
          appliedHigh: asNumber(point.appliedHigh),
          qoq: (point.qoq ?? {}) as Record<string, unknown>,
          yoy: (point.yoy ?? {}) as Record<string, unknown>,
        }));
      }
      const poam = byId.get('safeT.poamPipeline');
      if (poam) {
        tuSecurity.poams = poam.series.slice(-MAX_QUARTERS_CHART).map((point: Record<string, unknown>) => ({
          quarter: String(point.quarter),
          unmitigatedNoPoam: asNumber(point.unmitigatedNoPoam),
          poamOpenNotDeveloped: asNumber(point.poamOpenNotDeveloped),
          poamDevelopedNotMonitored: asNumber(point.poamDevelopedNotMonitored),
          poamMonitoredNotShared: asNumber(point.poamMonitoredNotShared),
          poamShared: asNumber(point.poamShared),
          qoq: (point.qoq ?? {}) as Record<string, unknown>,
          yoy: (point.yoy ?? {}) as Record<string, unknown>,
        }));
      }
      continue;
    }

    const org = payload.groupId === 'Skyway Range' ? skyway : payload.groupId === 'DronePort Network' ? droneport : null;
    if (!org) continue;

    org.goalsByMetric = collectGoalsByMetric(section.metrics);

    const client = byId.get('safeT.clientAcquisition');
    if (client) {
      org.clientAcquisition = client.series.slice(-MAX_QUARTERS_CHART).map((point: Record<string, unknown>) => ({
        quarter: String(point.quarter),
        engaged: asNumber(point.engaged),
        acquired: asNumber(point.acquired),
        internationalTestingAcquired: asNumber(point.internationalTestingAcquired),
        qoq: (point.qoq ?? {}) as Record<string, unknown>,
        yoy: (point.yoy ?? {}) as Record<string, unknown>,
      }));
    }

    const revenue = byId.get('safeT.revenue');
    if (revenue) {
      org.revenue = revenue.series.slice(-MAX_QUARTERS_CHART).map((point: Record<string, unknown>) => ({
        quarter: String(point.quarter),
        revenueNewClients: asNumber(point.revenueNewClients),
        revenueAllClients: asNumber(point.revenueAllClients),
        qoq: (point.qoq ?? {}) as Record<string, unknown>,
        yoy: (point.yoy ?? {}) as Record<string, unknown>,
      }));
    }

    const tulsa = byId.get('safeT.tulsaImpact');
    if (tulsa) {
      org.tulsa = tulsa.series.slice(-MAX_QUARTERS_CHART).map((point: Record<string, unknown>) => ({
        quarter: String(point.quarter),
        totalRevenue: asNumber(point.totalRevenue),
        tulsaRevenue: asNumber(point.tulsaRevenue),
        totalProducers: asNumber(point.totalProducers),
        tulsaProducers: asNumber(point.tulsaProducers),
        qoq: (point.qoq ?? {}) as Record<string, unknown>,
        yoy: (point.yoy ?? {}) as Record<string, unknown>,
      }));
      org.tulsaAllTime = {
        totalRevenue: asNumber(tulsa.allTime?.totalRevenue),
        tulsaRevenue: asNumber(tulsa.allTime?.tulsaRevenue),
        totalProducers: asNumber(tulsa.allTime?.totalProducers),
        tulsaProducers: asNumber(tulsa.allTime?.tulsaProducers),
      };
    }
  }

  const combined = recomputeCombined(skyway, droneport);

  return {
    skyway,
    droneport,
    combined,
    tuSecurity,
  };
}
